import type {Component, Editor} from 'grapesjs';
import {isTabletScreenWidth} from '@Utils/dom.util';
import {EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS, HIGHLIGHT_HOVER_CLASS} from '@EmailMakerReact/email-maker-react.constants';
import {getNumberProp} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {setDefaultSelection} from '@EmailMakerReact/lib/email-maker-selection-library';
import {disconnectToolbarDropdownObservers, isToolbarDropdownComponent, mountToolbarDropdowns} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/listeners/toolbar-dropdowns';
import type {ToolbarDropdownObservers} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/listeners/toolbar-dropdowns';

const RTE_TOOLBAR_SELECTOR = '.gjs-rte-toolbar';
const GJS_TOOLBAR_SELECTOR = '.gjs-toolbar';
const GJS_BADGE_SELECTOR = '.gjs-badge';
/** The second `.gjs-badge` under the editor container is the canvas-frame badge. */
const CANVAS_BADGE_INDEX = 1;
/** Marks the badge as visible for a mobile selection; the mobile CSS hide rule excludes this class. */
const MOBILE_SELECTED_BADGE_CLASS = 'is-mobile-selected';

export function initSelectionListeners(editor: Editor): () => void {
  /** Hierarchy components currently under selection focus. */
  const inFocus = new Set<Component>();
  const canvasBox = editor.getContainer();

  /** Observers for the currently selected TEXT/BUTTON component's toolbar dropdowns. Null when no such component is selected. */
  let activeToolbarObservers: ToolbarDropdownObservers | null = null;

  const clearSelectionFocus = (): void => {
    inFocus.forEach((component) => {
      disableFocus(inFocus, component);
    });
  };

  const onCanvasBoxClick = (e: Event): void => {
    if (!(e.target instanceof Element)) {
      return;
    }
    if (e.target.closest(RTE_TOOLBAR_SELECTOR) || e.target.closest(GJS_TOOLBAR_SELECTOR)) {
      return;
    }

    setDefaultSelection(editor);
  };

  /** Initialize/update hierarchy on adding new components. */
  const onComponentMount = (component: Component): void => {
    editor.getModel().skip(() => {
      disableFocus(inFocus, component);

      if (component.get(EMAIL_MAKER_PROPS.HIERARCHY_SELECTION)) {
        const root = getHierarchyRootForComponent(component);
        const isRootInFocus = inFocus.has(root);

        enableDescendantsSelection(component);
        if (isRootInFocus) {
          enableFocus(inFocus, component);
        }
      }
    });
  };

  /** Clear previous focused highlights and set new ones. */
  const onComponentSelected = (component: Component): void => {
    if (isTabletScreenWidth()) {
      showSelectedComponentBadgeOnMobile(editor, component);
    } else {
      hideComponentBadge(editor, component);
    }

    editor.getModel().skip(() => {
      clearSelectionFocus();
      if (component.get(EMAIL_MAKER_PROPS.HIERARCHY_SELECTION)) {
        setSelectionFocus(inFocus, component);
      }
    });

    disconnectToolbarDropdownObservers(activeToolbarObservers);
    activeToolbarObservers = isToolbarDropdownComponent(component) ? mountToolbarDropdowns(editor, component) : null;
  };

  /** When a component is deselected, allow the badge to be shown again and tear down toolbar dropdown observers. */
  const onComponentDeselected = (component: Component): void => {
    if (isTabletScreenWidth()) {
      hideMobileSelectedBadge(editor);
    } else {
      showComponentBadge(editor, component);
    }

    if (isToolbarDropdownComponent(component)) {
      disconnectToolbarDropdownObservers(activeToolbarObservers);
      activeToolbarObservers = null;
    }
  };

  /** If a component is hovered, show the badge; if it is selected or being edited, hide it. */
  const onComponentHover = (component: Component): void => {
    if (isTabletScreenWidth()) {
      return;
    }

    const isEditing = editor.getEditing()?.getId() === component?.getId();
    const isSelected = editor.getSelected()?.getId() === component?.getId();

    if (isEditing || isSelected) {
      hideComponentBadge(editor, component);
    } else {
      showComponentBadge(editor, component);
    }
  };

  /** Update existing components being moved in/out of a hierarchy. */
  const onComponentDragEnd = ({target, parent}: {target: Component; parent: Component}): void => {
    editor.getModel().skip(() => {
      const isRoot = Boolean(target.get(EMAIL_MAKER_PROPS.HIERARCHY_ROOT));
      const targetHasHierarchy = Boolean(target.get(EMAIL_MAKER_PROPS.HIERARCHY_SELECTION));
      const parentHasHierarchy = Boolean(parent.get(EMAIL_MAKER_PROPS.HIERARCHY_SELECTION));
      const noHierarchy = !targetHasHierarchy && !parentHasHierarchy;

      if (isRoot || noHierarchy) {
        return;
      }

      if (parentHasHierarchy) {
        enableFocus(inFocus, target);
        target.set(EMAIL_MAKER_PROPS.HIERARCHY_SELECTION, true);
      } else {
        disableFocus(inFocus, target);
        target.set(EMAIL_MAKER_PROPS.HIERARCHY_SELECTION, false);
      }

      forceReselection(editor, target);
    });
  };

  /** Disable single-column selection from sections. */
  const onColumnsPresetUpdate = (component: Component): void => {
    editor.getModel().skip(() => {
      const selectableColumns = (getNumberProp(component, EMAIL_MAKER_PROPS.COLUMNS_PRESET) ?? 0) > 1;

      component.findType(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL)?.forEach((column: Component) => {
        updateSingleColumnHierarchy(inFocus, column, selectableColumns);
      });
    });
  };

  const columnsPresetUpdateEvent = `component:update:${EMAIL_MAKER_PROPS.COLUMNS_PRESET}`;

  canvasBox?.addEventListener('click', onCanvasBoxClick);
  editor.on('component:mount', onComponentMount);
  editor.on('component:selected', onComponentSelected);
  editor.on('component:deselected', onComponentDeselected);
  editor.on('component:hover', onComponentHover);
  editor.on('component:drag:end', onComponentDragEnd);
  editor.on(columnsPresetUpdateEvent, onColumnsPresetUpdate);

  return (): void => {
    canvasBox?.removeEventListener('click', onCanvasBoxClick);
    editor.off('component:mount', onComponentMount);
    editor.off('component:selected', onComponentSelected);
    editor.off('component:deselected', onComponentDeselected);
    editor.off('component:hover', onComponentHover);
    editor.off('component:drag:end', onComponentDragEnd);
    editor.off(columnsPresetUpdateEvent, onColumnsPresetUpdate);
    inFocus.clear();
    disconnectToolbarDropdownObservers(activeToolbarObservers);
    activeToolbarObservers = null;
  };
}

/** Toggles a column's selection and enables/disables its children instead. */
function updateSingleColumnHierarchy(inFocus: Set<Component>, column: Component, selectable: boolean): void {
  column.set('hoverable', true);

  if (selectable) {
    enableFocus(inFocus, column);
    enableSelection(column);
    enableDescendantsSelection(column);
  } else {
    disableFocus(inFocus, column);
  }
}

function forceReselection(editor: Editor, component: Component): void {
  if (!component) {
    return;
  }

  editor.selectRemove(component);
  editor.select(component);
}

/** For the given component, enables selecting children and brings parent hierarchies into focus. */
function setSelectionFocus(inFocus: Set<Component>, component: Component): void {
  if (!component) {
    return;
  }

  enableChildrenFocus(inFocus, component);
  if (component.get('hoverable')) {
    enableFocus(inFocus, component);
  }

  const parent = component.parent();
  if (parent?.get(EMAIL_MAKER_PROPS.HIERARCHY_SELECTION)) {
    setSelectionFocus(inFocus, parent);
  }
}

/** Enables selection on the first hoverable children. */
function enableChildrenFocus(inFocus: Set<Component>, component: Component): void {
  component.components()?.forEach((child: Component) => {
    if (!child.get('hoverable')) {
      enableChildrenFocus(inFocus, child);
    } else {
      enableFocus(inFocus, child);
    }
  });
}

function enableDescendantsSelection(component: Component): void {
  component?.removeClass(HIGHLIGHT_HOVER_CLASS);
  component?.components()?.forEach((child: Component) => {
    if (child.get('hoverable')) {
      enableSelection(child);
    }
    enableDescendantsSelection(child);
  });
}

function enableSelection(component: Component): void {
  component.set('selectable', true);
}

function enableFocus(inFocus: Set<Component>, component: Component): void {
  inFocus.add(component);
  component.set('highlightable', true);
}

function disableFocus(inFocus: Set<Component>, component: Component): void {
  inFocus.delete(component);
  component.set('highlightable', false);
}

function getHierarchyRootForComponent(component: Component): Component {
  const isRoot = Boolean(component.get(EMAIL_MAKER_PROPS.HIERARCHY_ROOT));
  const parent = component.parent();
  const hasHierarchicalParent = Boolean(parent?.get(EMAIL_MAKER_PROPS.HIERARCHY_SELECTION));

  return isRoot || !parent || !hasHierarchicalParent ? component : getHierarchyRootForComponent(parent);
}

/** Badge changes run outside the undo history. */
function hideComponentBadge(editor: Editor, component: Component): void {
  editor.UndoManager.stop();
  getBadgeElement(editor)?.style?.setProperty('display', 'none', 'important');
  component?.set('badgable', false, {silent: true});
  editor.UndoManager.start();
}

function showComponentBadge(editor: Editor, component: Component): void {
  editor.UndoManager.stop();
  component?.set('badgable', true, {silent: true});
  getBadgeElement(editor)?.style?.setProperty('display', 'block');
  editor.UndoManager.start();
}

/**
 * On mobile, position the shared badge at the selected component via GrapesJS' own local-tools flow and flag it visible
 * for the mobile CSS gate. Visibility is driven purely by the class; any inline display left by the desktop show/hide
 * path is cleared so the gate governs.
 */
function showSelectedComponentBadgeOnMobile(editor: Editor, component: Component): void {
  editor.UndoManager.stop();
  component?.set('badgable', true, {silent: true});
  positionBadgeAtComponent(editor, component);
  const badge = getBadgeElement(editor);
  badge?.style?.removeProperty('display');
  badge?.classList.add(MOBILE_SELECTED_BADGE_CLASS);
  editor.UndoManager.start();
}

/**
 * Runs the badge to `component` through GrapesJS' local-tools flow. `setHovered` bails on a component whose `hoverable`
 * is falsy when `useValid` is false (and would otherwise climb to a hoverable ancestor), so `hoverable` is forced on
 * across the synchronous call and restored — keeping the badge on the exact selected component regardless of hierarchy.
 */
function positionBadgeAtComponent(editor: Editor, component: Component): void {
  const wasHoverable = Boolean(component.get('hoverable'));
  component.set('hoverable', true, {silent: true});
  editor.getModel().setHovered(component, {useValid: false});
  component.set('hoverable', wasHoverable, {silent: true});
}

/** On mobile, clear the mobile-selected flag on deselection; the CSS gate re-hides the badge once the class is gone. */
function hideMobileSelectedBadge(editor: Editor): void {
  editor.UndoManager.stop();
  getBadgeElement(editor)?.classList.remove(MOBILE_SELECTED_BADGE_CLASS);
  editor.getModel().setHovered(null);
  editor.UndoManager.start();
}

function getBadgeElement(editor: Editor): HTMLElement | undefined {
  const badge = editor.getContainer()?.querySelectorAll(GJS_BADGE_SELECTOR)[CANVAS_BADGE_INDEX];
  return badge instanceof HTMLElement ? badge : undefined;
}
